import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { Command } from "cmdk"
import { Dialog, DialogContent } from "./ui/dialog"
import { cn } from "../lib/utils"
import { Search } from "lucide-react"
type NavItem = {
 label: string
 link?: string
 subItems?: NavItem[]
 type?:"section"}

function flattenNav(items: NavItem[]): Array<{ label: string; link: string }> {
 const out: Array<{ label: string; link: string }> = []
 for (const item of items) {
 if ((item as any).type === "section") continue
 if (item.link) out.push({ label: item.label, link: item.link })
 if (item.subItems?.length) out.push(...flattenNav(item.subItems))
 }
 return out
}

export function CommandPalette({ menuItems }: { menuItems: NavItem[] }) {
 const { t } = useTranslation()
 const navigate = useNavigate()
 const location = useLocation()
 const [open, setOpen] = useState(false)
 const [query, setQuery] = useState("")

 const commands = useMemo(() => flattenNav(menuItems), [menuItems])

 useEffect(() => {
 const onKeyDown = (e: KeyboardEvent) => {
 const key = e.key ?? ""
const isK = key.toLowerCase() === "k"
const isCmdK = (e.metaKey || e.ctrlKey) && isK
 if (!isCmdK) return
 e.preventDefault()
 setOpen((v) => !v)
 }
 window.addEventListener("keydown", onKeyDown)
 return () => window.removeEventListener("keydown", onKeyDown)
 }, [])

 useEffect(() => {
 // Close palette on navigation.
 setOpen(false)
 setQuery("")
 }, [location.pathname, location.search])

 return (
 <Dialog open={open} onOpenChange={setOpen}>
 <DialogContent className="p-0 max-w-xl overflow-hidden">
 <Command
 className="flex flex-col"filter={(value, search) => {
 if (search == null || search === "") return 1
 const v = String(value ?? "").toLowerCase()
 const s = String(search).toLowerCase()
 return v.includes(s) ? 1 : 0
 }}
 >
 <div className="flex items-center gap-2 border-b px-4 py-3">
 <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
 <Command.Input
 value={query}
 onValueChange={setQuery}
 placeholder={t('ui.commandPalette.placeholder')} className={cn(
"w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground")}
 autoFocus
 />
 </div>
 <Command.List className="max-h-[420px] overflow-auto p-2">
 <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
 {t('ui.commandPalette.noResults')}
 </Command.Empty>
 {commands.map((c) => (
 <Command.Item
 key={c.link}
 value={`${c.label} ${c.link}`}
 onSelect={() => navigate(c.link)}
 className={cn(
"flex cursor-pointer items-center rounded-lg px-3 py-2 text-sm outline-none",
"aria-selected:bg-secondary aria-selected:text-foreground",
"text-foreground/90")}
 >
 <span className="flex-1">{c.label}</span>
 <span className="text-xs text-muted-foreground" dir="ltr">
 {c.link}
 </span>
 </Command.Item>
 ))}
 </Command.List>
 <div className="border-t px-4 py-2 text-xs text-muted-foreground">
 {t('ui.commandPalette.footer')}
 </div>
 </Command>
 </DialogContent>
 </Dialog>
 )
}

